import { AssignmentReferenceBookingsForm } from "@/forms/assignment-reference-bookings-form";
import { ModalLoadFailed } from "@/components/modals";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { PartnerTimelineAssignmentReferenceApproveButton } from "@/sites/partners/partner/partner-timeline/partner-timeline-assignment-reference-approve-button";
import { Alert } from "@msuite/ui/alert";
import { Button } from "@msuite/ui/button";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, Modal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { ResponseError } from "@msuite/utils/errors";
import { moment } from "@msuite/utils/date";
import {
	getDefaultBookingAdjustment,
	getDefaultBookings,
	getSettlementRecordState,
} from "@msuite/utils/partnerships";
import {
	type AssignmentReferenceBookingsSchema,
	assignmentReferenceBookingsSchema,
} from "@msuite/utils/schemas";
import { generateId, guardString } from "@msuite/utils/strings";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { AssignmentReferenceBookingsFooter } from "./assignment-reference-bookings-footer";
import { AssignmentReferenceBookingsHeader } from "./assignment-reference-bookings-header";
import { AssignmentReferenceBookingsStaleAlert } from "./assignment-reference-bookings-stale-alert";
import { AssignmentReferenceBookingsSubmitButton } from "./assignment-reference-bookings-submit-button";
import { AssignmentReferenceBookingsModalContext } from "./context";
import { useAgreementsData } from "./hooks/use-agreements-data";
import { useAssignmentReferenceData } from "./hooks/use-assignment-reference-data";

const twoColumnWidth = "60vw";
/**
 * The statements alone, each input as wide as it stands beside the host's column: half the
 * two-column width, plus half of what does not halve with it — the 6rem of labels and the 70px of
 * card insets, borders and scrollbar gutter the grid sits inside. The gap between the columns
 * cancels out.
 */
const oneColumnWidth = "calc(30vw + (6rem + 70px) / 2)";

/** Props Interface */
interface AssignmentReferenceBookingsModalProps {
	isOpen: boolean;
	onClose: () => void;
	referenceId: string;
	partnershipId: string;
	date: string;
	onComplete: () => Promise<void>;
}

export function AssignmentReferenceBookingsModal({
	isOpen,
	onClose,
	referenceId,
	partnershipId,
	date,
	onComplete,
}: AssignmentReferenceBookingsModalProps) {
	/** State */
	// Tracks IDs of bookings the host added during this session (not from server).
	// Used in handleOnSubmit to promote adjustment→base for host-created bookings.
	const hostCreatedBookingIds = useRef(new Set<string>());
	const [hasSavedBookings, setHasSavedBookings] = useState(false);
	// The version of the record the form was read at, sent back with the save so a record that
	// moved in between refuses it rather than being overwritten from this copy.
	const version = useRef("");
	const [isStale, setIsStale] = useState(false);
	const [isReloading, setIsReloading] = useState(false);

	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(loadBookings);
	const methods = useForm({
		resolver: resolver(assignmentReferenceBookingsSchema),
		defaultValues,
	});
	// Read on every render, the first one included. The loading flag is a proxy that reports a
	// change only to a component that has already read it, and the guard below returns before
	// the form is in the tree — a load finishing during that guard would otherwise leave the
	// spinner behind for good.
	const isFormLoading = methods.formState.isLoading;
	const { organization } = useAuth();
	const asyncModal = useAsyncModal();
	const qc = useQueryClient();

	/** Query */
	const { assignmentReferenceData, isLoading, isError, refetch } = useAssignmentReferenceData({
		partnershipId,
		referenceId,
	});
	const { agreementsData } = useAgreementsData({ partnershipId, date });

	/** Functions */
	async function loadBookings(): Promise<AssignmentReferenceBookingsSchema> {
		const orgId = organization?.id;
		if (!orgId) throw new Error("No organization found");
		const response = await client.organizations[":orgId"].partnerships[
			":partnershipId"
		].timeline.reference[":referenceId"].bookings.$get({
			param: { orgId, partnershipId, referenceId },
		});
		const data = await responseHandler(response);
		version.current = data.version;
		const bookings = guardArray(data.bookings);
		// The record holds bookings only when the list has rows — `hasNonNull` would answer for
		// the array itself and count an empty list as saved bookings.
		const hasRows = bookings.length > 0;
		setHasSavedBookings(hasRows);
		if (hasRows) return { bookings };
		// The first row of an empty record belongs to whoever fills it in. Registered here, it is
		// promoted like any row the host adds — and the promotion only runs for the host.
		const defaults = getDefaultBookings(date);
		for (const booking of defaults.bookings) hostCreatedBookingIds.current.add(booking.id);
		return defaults;
	}

	function handleOnAddBooking() {
		const bookings = methods.getValues("bookings");
		const lastBooking = Object.assign({}, bookings[bookings.length - 1]);
		if (!lastBooking.id) {
			const _default = getDefaultBookings(date);
			for (const booking of _default.bookings) hostCreatedBookingIds.current.add(booking.id);
			methods.setValue("bookings", _default.bookings);
			return;
		}
		lastBooking.id = generateId();
		lastBooking.userId = null;
		lastBooking.ranking++;
		lastBooking.adjustment = getDefaultBookingAdjustment(lastBooking);
		if (assignmentReferenceData?.role === "host") {
			hostCreatedBookingIds.current.add(lastBooking.id);
		}
		methods.setValue("bookings", [...bookings, lastBooking]);
	}

	async function handleOnClose() {
		if (methods.formState.isDirty) {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
		}
		onClose();
		methods.reset();
	}

	async function handleOnSubmit(json: AssignmentReferenceBookingsSchema) {
		try {
			if (!organization) throw new Error("No organization found");

			let bookings = json.bookings;

			if (assignmentReferenceData?.role === "host") {
				bookings = bookings.map((booking) => {
					if (!hostCreatedBookingIds.current.has(booking.id)) return booking;
					const adj = booking.adjustment;
					if (!adj) return booking;
					return {
						...booking,
						checkIn: adj.checkIn ?? booking.checkIn,
						checkOut: adj.checkOut ?? booking.checkOut,
						pause: adj.pause ?? booking.pause,
					};
				});
			}

			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].timeline.reference[":referenceId"].bookings.$post({
				param: {
					orgId: guardString(organization?.id),
					partnershipId,
					referenceId,
				},
				json: { bookings, version: version.current },
			});
			await responseHandler(response);
			// The record's meta and its timeline card are cached for a minute; a save changes the
			// record's review state, so the next open has to read it fresh.
			await qc.invalidateQueries({ predicate: invalidate(["partner"]) });
			onClose();
			await onComplete();
			methods.reset();
		} catch (error) {
			// `responseHandler` has already toasted the refusal; the modal stays open on the edits.
			// A record that moved underneath this copy also says so in the modal and offers to reload.
			if (error instanceof ResponseError && error.key === "partners.bookings_changed") {
				setIsStale(true);
			}
		}
	}

	async function handleOnReload() {
		try {
			setIsReloading(true);
			hostCreatedBookingIds.current.clear();
			methods.reset(await loadBookings());
			setIsStale(false);
		} finally {
			setIsReloading(false);
		}
	}

	async function handleOnApprovalChange() {
		await refetch();
		await onComplete();
	}

	/** Guard */
	// The role decides what the modal may show at all, so there is no form to
	// build without it. The modal still opens: a refused meta read has to say so,
	// because a click that renders nothing reads as a click that did nothing.
	if (!assignmentReferenceData?.role) {
		return (
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("partners.bookings")}
				width="60vw"
				isLoading={isLoading && !isError}
			>
				<ModalLoadFailed onClose={onClose} />
			</Modal>
		);
	}

	/** Computed */
	const reference = assignmentReferenceData.reference;
	const isApproved = reference?.isApproved ?? false;
	const isReviewed = reference?.isReviewed ?? false;
	const state = getSettlementRecordState({
		isApproved,
		isReviewed,
		hasBookings: hasSavedBookings,
	});
	// The host owns the project, so the project's organisation is the side that approves.
	const hostOrgId = guardString(assignmentReferenceData.project?.orgId);
	const canApprove = assignmentReferenceData.role === "host" && hasSavedBookings;
	const hasHostColumn = assignmentReferenceData.role === "host" || hasSavedBookings;

	/** Render */
	return (
		<AssignmentReferenceBookingsModalContext.Provider
			value={{
				role: assignmentReferenceData.role,
				partnerOrgId: assignmentReferenceData.partnerOrgId,
				isApproved,
				hasSavedBookings,
				hostCreatedBookingIds: hostCreatedBookingIds.current,
				isReviewed,
				hasHostColumn,
			}}
		>
			<FormProvider {...methods}>
				<Modal
					isOpen={isOpen}
					onClose={handleOnClose}
					title={t("partners.bookings")}
					width={hasHostColumn ? twoColumnWidth : oneColumnWidth}
					isLoading={isFormLoading && !loadFailed}
					onSubmit={methods.handleSubmit(handleOnSubmit)}
				>
					{loadFailed ? (
						<ModalLoadFailed onClose={onClose} />
					) : (
						<>
							<Modal.Body
								variant="withoutPadding"
								gap={0}
							>
								<AssignmentReferenceBookingsHeader
									project={assignmentReferenceData?.project}
									tickets={assignmentReferenceData?.referenceTickets}
									state={state}
								/>
								{isStale && (
									<AssignmentReferenceBookingsStaleAlert
										isReloading={isReloading}
										onReload={handleOnReload}
									/>
								)}
								{/* An approved record takes no change from either side — the server refuses it —
								so the modal says why it is read-only instead of only hiding its save. */}
								{isApproved && (
									<div className="p-4">
										<Alert
											variant="success"
											title={
												reference?.approvedAt
													? `${t("general.approved")} · ${moment(reference.approvedAt).longFormat()}`
													: t("general.approved")
											}
											description={t("partners.approved_reference_hint")}
										/>
									</div>
								)}
								{/* The bookings and their totals scroll as one region, so both stand in the
								same width whether or not its scrollbar takes some of it — the totals
								line up under the booking columns. The region is white, so each grey
								booking card stands apart from the next. */}
								<div className="overflow-y-auto card-bg v-stack gap-0">
									<AssignmentReferenceBookingsForm date={date} />
									<AssignmentReferenceBookingsFooter
										agreement={agreementsData?.active}
										onAddBooking={handleOnAddBooking}
									/>
								</div>
							</Modal.Body>
							<Modal.Footer>
								{canApprove && reference && (
									<PartnerTimelineAssignmentReferenceApproveButton
										reference={reference}
										partnershipId={partnershipId}
										hostOrgId={hostOrgId}
										hasBookings={hasSavedBookings}
										onComplete={handleOnApprovalChange}
									/>
								)}
								<Button
									variant="ghost"
									onClick={handleOnClose}
								>
									{t("general.cancel")}
								</Button>
								{!isApproved && <AssignmentReferenceBookingsSubmitButton />}
							</Modal.Footer>
						</>
					)}
				</Modal>
			</FormProvider>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("general.dismiss_unsaved_changes")}
				/>
			)}
		</AssignmentReferenceBookingsModalContext.Provider>
	);
}
